import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { getCachedSignedUrl, setCachedSignedUrl } from '../utils/imageCache';
import { mapWithConcurrency } from '../utils/concurrency';

/**
 * Últimas creaciones REALES del usuario — ADR-2026-07-31-shell-nuevo-datos-reales.
 *
 * La home nueva mostraba `CREACIONES_RECIENTES` de `contenidoAtlas`, que son las
 * imágenes de demo del alta (`GENERATION_RESULTS`) y de los ejemplos por modo. Eso
 * era correcto para un preview —había que comparar tres estilos con contenido, no
 * con media pantalla en blanco— pero en la plataforma es una sección que se llama
 * "Lo último que generaste" y muestra cosas que el usuario nunca generó.
 *
 * Lee lo MISMO que `/redes-sociales/imagenes` (`GeneratedImagesHistory`): la tabla
 * `imagenes_generadas` filtrada por empresa y `active = true`, y las miniaturas del
 * bucket `generadas` bajo `{idCompany}/opt/{file_name}`, con el `full/` como
 * respaldo cuando el thumbnail todavía no existe. Los mismos paths y la misma
 * cache de URLs firmadas que el historial, así que abrir la home no vuelve a
 * firmar lo que el historial ya firmó (y al revés).
 */

/** Máximo de operaciones de Storage en vuelo — el mismo tope que `preloadGeneratedImages`. */
const STORAGE_CONCURRENCY = 4;

/** Cuánto dura una URL firmada, en segundos. Igual que en el historial. */
const TTL_FIRMA = 3600;

export interface CreacionReciente {
  /** `imagenes_generadas.id` (uuid). Se usa para abrirla en el historial. */
  id: string;
  /** URL firmada de la miniatura (o del full si no hay `opt/`). */
  src: string;
  alt: string;
  /** `true` cuando el resultado es un video — se le pinta el play encima. */
  esVideo: boolean;
  /**
   * Proporción ancho/alto, para dibujar la miniatura con la forma que tiene en vez
   * de recortarla a una fija — ADR-2026-08-03-creaciones-recientes-respetan-su-forma.
   *
   * Sale de `imagenes_generadas.formato` ("4:5", "9:16", "1:1"…), que se guarda al
   * generar. `null` cuando la fila no lo trae (una de cada cuatro): ahí la mide el
   * componente cuando la imagen carga.
   *
   * ⚠️ **En los videos es sólo una apuesta.** Lo que se guarda es el formato que
   * pidió el flujo, no el del archivo: los de "animar imagen" heredan la forma de la
   * imagen de origen y aun así quedan anotados como `9:16`
   * (ADR-2026-08-03-el-formato-de-los-videos-no-es-de-fiar). Quien dibuje un video
   * tiene que medir la miniatura y quedarse con lo medido.
   */
  ratio: number | null;
}

interface FilaGenerada {
  id: string;
  file_name: string | null;
  tipo: string | null;
  formato: string | null;
  /**
   * Proporción de los VIDEOS — ADR-2026-08-03-ratio-de-los-videos-sale-de-datos-generacion.
   *
   * La columna `formato` la llena el flujo de imágenes; el de video guarda lo suyo
   * dentro de `datos_generacion`, con dos nombres según por dónde se generó
   * (`Format` en el flujo viejo, `aspect_ratio` en el de influencer). Sobre 2.225
   * videos activos: 76 tienen `formato`, 1.381 sólo `Format` y 35 sólo
   * `aspect_ratio`.
   *
   * Se piden como claves sueltas del jsonb y NO la columna entera: `datos_generacion`
   * llega hasta 475 KB en una fila (lleva prompts y escenas), y traerla para leer un
   * "9:16" convertiría la carga de la home en megabytes.
   */
  fmt_gen: string | null;
  fmt_gen_alt: string | null;
  created_at: string;
  variantes?: { nombre?: string | null } | null;
}

/**
 * "4:5" → 0.8. Devuelve `null` si el valor no es un par de números positivos: la
 * columna es texto libre y una miniatura con `aspect-ratio: NaN` desaparece.
 */
function ratioDeFormato(formato: string | null): number | null {
  if (!formato) return null;
  const [a, b] = formato.split(':').map((n) => Number(n.trim()));
  if (!Number.isFinite(a) || !Number.isFinite(b) || a <= 0 || b <= 0) return null;
  return a / b;
}

/** Mismo criterio que el historial: por `tipo`, con la extensión como respaldo. */
function esVideo(fila: FilaGenerada): boolean {
  return (
    fila.tipo === 'video' ||
    fila.tipo === 'video_pro' ||
    !!fila.file_name?.toLowerCase().endsWith('.mp4')
  );
}

export interface CreacionesRecientes {
  items: CreacionReciente[];
  cargando: boolean;
  /**
   * Hay creaciones más allá de las `limite` que se devuelven —
   * ADR-2026-08-03-ver-mas-solo-si-hay-algo-mas.
   *
   * Se sabe pidiendo UNA de más y devolviéndola de menos: si volvieron `limite + 1`,
   * hay al menos una que no entró en `items`. Sin esto, "traje 6" y "hay exactamente
   * 6" son indistinguibles, y la home no puede decidir si mostrar "Ver más".
   */
  hayMas: boolean;
}

/**
 * @param idCompany Empresa del usuario. Mientras sea `null` el hook no consulta —
 *   lo resuelve `useCuentaAtlas`, que es quien ya leyó `profiles`.
 * @param limite Cuántas traer. La home muestra 6.
 * @param idMarca Marca activa. Con marca, la sección muestra sólo lo generado para
 *   ELLA — ADR-2026-08-03-creaciones-recientes-por-marca. `null` = sin filtro, que es
 *   lo que corresponde mientras la marca todavía no resolvió y en cuentas de una
 *   sola marca.
 */
export function useCreacionesRecientes(
  idCompany: string | null,
  limite = 6,
  idMarca: string | null = null,
): CreacionesRecientes {
  const [items, setItems] = useState<CreacionReciente[]>([]);
  const [hayMas, setHayMas] = useState(false);
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    if (!idCompany) {
      // Sin empresa todavía no se puede afirmar que no haya creaciones: se sigue
      // considerando "cargando" para que la home no parpadee el estado vacío
      // antes de tiempo.
      return;
    }
    let cancelado = false;

    (async () => {
      try {
        setCargando(true);
        // La marca no está en `imagenes_generadas`: se deduce del producto, así que
        // filtrarla obliga a subir por `variantes → productos.brand_id` con dos
        // `!inner`. Es el MISMO camino que usa el filtro del historial
        // (`GeneratedImagesHistory`), a propósito: si las dos pantallas dedujeran la
        // marca distinto, la preview de la home y su "Ver más" mostrarían conjuntos
        // que no coinciden.
        //
        // Consecuencia del `!inner`: las generaciones SIN producto quedan afuera
        // (~2,8% de las activas). Es lo mismo que ya le pasa al historial filtrado.
        const select = idMarca
          ? 'id, file_name, tipo, formato, fmt_gen:datos_generacion->>Format, fmt_gen_alt:datos_generacion->>aspect_ratio, created_at, variantes:variantes!inner(nombre, productos!inner(brand_id))'
          : 'id, file_name, tipo, formato, fmt_gen:datos_generacion->>Format, fmt_gen_alt:datos_generacion->>aspect_ratio, created_at, variantes:variantes(nombre)';
        let query = supabase
          .from('imagenes_generadas')
          .select(select)
          .eq('id_company', idCompany)
          .eq('active', true);
        if (idMarca) query = query.eq('variantes.productos.brand_id', idMarca);
        const { data, error } = await query
          .order('created_at', { ascending: false })
          // Una de más que las que se devuelven: es el sondeo de `hayMas`.
          .range(0, limite);

        if (error) throw error;
        if (cancelado) return;

        const filas = ((data ?? []) as unknown as FilaGenerada[]).filter(
          (f) => !!f.file_name,
        );

        const resueltas = await mapWithConcurrency(
          filas,
          STORAGE_CONCURRENCY,
          async (fila): Promise<CreacionReciente | null> => {
            const fileName = fila.file_name as string;
            // El thumbnail de un video es el PNG homónimo, igual que en el
            // historial: el bucket no guarda un `opt/` en mp4.
            const nombreThumb = esVideo(fila)
              ? fileName.replace(/\.(mp4|webm|mov)$/i, '.png')
              : fileName;
            const pathOpt = `${idCompany}/opt/${nombreThumb}`;
            const pathFull = `${idCompany}/full/${fileName}`;

            let src = getCachedSignedUrl(pathOpt) ?? '';
            if (!src) {
              const { data: firmada } = await supabase.storage
                .from('generadas')
                .createSignedUrl(pathOpt, TTL_FIRMA);
              if (firmada?.signedUrl) {
                src = firmada.signedUrl;
                setCachedSignedUrl(pathOpt, src);
              }
            }

            // Sin `opt/` (carrera con el final del job, o generaciones viejas) se
            // cae al full. Para un video el full es el mp4, que como `src` de un
            // `<img>` no pinta nada: en ese caso se descarta la fila en vez de
            // dejar un hueco roto en la grilla.
            if (!src && !esVideo(fila)) {
              src = getCachedSignedUrl(pathFull) ?? '';
              if (!src) {
                const { data: firmadaFull } = await supabase.storage
                  .from('generadas')
                  .createSignedUrl(pathFull, TTL_FIRMA);
                if (firmadaFull?.signedUrl) {
                  src = firmadaFull.signedUrl;
                  setCachedSignedUrl(pathFull, src);
                }
              }
            }

            if (!src) return null;
            return {
              id: fila.id,
              src,
              alt: fila.variantes?.nombre ?? 'Creación de Atlas',
              esVideo: esVideo(fila),
              // Cadena de respaldo: la columna primero (es la de las imágenes) y
              // después las dos claves donde lo guarda el flujo de video. Lo que no
              // resuelva por acá lo mide el componente cuando carga la miniatura.
              ratio:
                ratioDeFormato(fila.formato) ??
                ratioDeFormato(fila.fmt_gen) ??
                ratioDeFormato(fila.fmt_gen_alt),
            };
          },
        );

        if (cancelado) return;
        const utiles = resueltas.filter((r): r is CreacionReciente => r !== null);
        // El sondeo se hace sobre las filas CRUDAS y no sobre `utiles`: una fila
        // puede caerse acá porque su archivo no resolvió, y eso no significa que no
        // haya más creaciones en la base.
        setHayMas(filas.length > limite);
        setItems(utiles.slice(0, limite));
      } catch {
        // Falla cerrado: sin creaciones que mostrar, la home pinta el estado
        // vacío en vez de romperse.
        if (!cancelado) {
          setItems([]);
          setHayMas(false);
        }
      } finally {
        if (!cancelado) setCargando(false);
      }
    })();

    return () => {
      cancelado = true;
    };
    // `idMarca` en las deps: cambiar de marca en la sidebar tiene que volver a pedir,
    // que es todo el punto del filtro.
  }, [idCompany, limite, idMarca]);

  return { items, cargando, hayMas };
}

export default useCreacionesRecientes;
